import React, { useState, useEffect, useCallback } from 'react';
import API_URL from '../config';
import { useToast, parseApiError } from '../context/ToastContext';

function Traces({ token }) {
  const toast = useToast();
  const [traces, setTraces] = useState([]);
  const [total, setTotal] = useState(0);
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [selectedSpan, setSelectedSpan] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTraces = useCallback(async () => {
    const params = new URLSearchParams({ limit: '50' });
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    try {
      const res = await fetch(`${API_URL}/api/protected/traces?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setTraces(data.traces || []);
        setTotal(data.total || 0);
      }
    } catch (e) {}
  }, [token, search, statusFilter]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/protected/traces/stats?period=24h`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) {}
  };

  useEffect(() => { fetchTraces(); fetchStats(); }, [fetchTraces]);
  useEffect(() => { const i = setInterval(fetchTraces, 5000); return () => clearInterval(i); }, [fetchTraces]);

  const openTrace = async (traceId) => {
    try {
      const res = await fetch(`${API_URL}/api/protected/traces/${traceId}`, { headers });
      if (!res.ok) throw new Error(await parseApiError(res, 'Failed to load trace'));
      const data = await res.json();
      setSelectedTrace(data);
      setSelectedSpan(null);
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const statusBadge = (status) => {
    const map = { completed: 'badge-success', running: 'badge-info', error: 'badge-error' };
    return <span className={`badge ${map[status] || 'badge-neutral'}`}>{status}</span>;
  };

  const spanTypeColor = (type) => {
    const map = { llm: 'purple', tool: 'warning', chain: 'info', retrieval: 'success', generic: 'neutral' };
    return map[type] || 'neutral';
  };

  const formatDuration = (ms) => {
    if (!ms && ms !== 0) return '-';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  // Build tree structure from spans
  const buildTree = (spans) => {
    if (!spans || spans.length === 0) return [];
    const map = {};
    const roots = [];
    spans.forEach(s => { map[s.span_id] = { ...s, children: [] }; });
    spans.forEach(s => {
      if (s.parent_span_id && map[s.parent_span_id]) {
        map[s.parent_span_id].children.push(map[s.span_id]);
      } else {
        roots.push(map[s.span_id]);
      }
    });
    return roots;
  };

  const renderSpanTree = (nodes, depth = 0) => {
    return nodes.map(node => (
      <React.Fragment key={node.span_id}>
        <tr
          className="clickable-row"
          onClick={() => setSelectedSpan(selectedSpan?.span_id === node.span_id ? null : node)}
          style={{ background: selectedSpan?.span_id === node.span_id ? 'var(--accent-subtle)' : undefined }}
        >
          <td style={{ paddingLeft: 12 + depth * 20 }}>
            <span style={{ color: 'var(--text-muted)', marginRight: 6 }}>{depth > 0 ? '\u2514' : '\u25CF'}</span>
            <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{node.name}</span>
          </td>
          <td><span className={`badge badge-${spanTypeColor(node.type)}`}>{node.type}</span></td>
          <td>{statusBadge(node.status)}</td>
          <td className="text-mono text-sm">{formatDuration(node.duration_ms)}</td>
          <td className="text-sm">{node.tokens_in + node.tokens_out > 0 ? (node.tokens_in + node.tokens_out).toLocaleString() : '-'}</td>
          <td className="text-sm">{node.cost > 0 ? `$${node.cost.toFixed(4)}` : '-'}</td>
        </tr>
        {node.children.length > 0 && renderSpanTree(node.children, depth + 1)}
      </React.Fragment>
    ));
  };

  if (selectedTrace) {
    const tree = buildTree(selectedTrace.spans);
    return (
      <>
        <div className="page-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button className="btn-ghost" onClick={() => { setSelectedTrace(null); setSelectedSpan(null); }}>&larr; Back</button>
              <div className="page-title">{selectedTrace.name || 'Trace'}</div>
              {statusBadge(selectedTrace.status)}
            </div>
            <div className="page-subtitle">
              {selectedTrace.trace_id} {selectedTrace.agent_name ? `\u00B7 ${selectedTrace.agent_name}` : ''}
            </div>
          </div>
        </div>
        <div className="page-body">
          <div className="metrics-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
            <div className="metric-card blue">
              <div className="metric-label">Spans</div>
              <div className="metric-value blue">{selectedTrace.span_count}</div>
            </div>
            <div className="metric-card purple">
              <div className="metric-label">Tokens</div>
              <div className="metric-value purple">{selectedTrace.total_tokens.toLocaleString()}</div>
            </div>
            <div className="metric-card yellow">
              <div className="metric-label">Cost</div>
              <div className="metric-value yellow">${selectedTrace.total_cost.toFixed(4)}</div>
            </div>
            <div className="metric-card green">
              <div className="metric-label">Started</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{new Date(selectedTrace.started_at).toLocaleString()}</div>
            </div>
            <div className="metric-card red">
              <div className="metric-label">Status</div>
              <div style={{ marginTop: 4 }}>{statusBadge(selectedTrace.status)}</div>
              {selectedTrace.error_message && <div className="text-xs" style={{ color: 'var(--error)', marginTop: 4 }}>{selectedTrace.error_message}</div>}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">Execution Graph</div>
              <div className="text-sm text-muted">{selectedTrace.spans?.length || 0} spans</div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Span</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Tokens</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {tree.length > 0 ? renderSpanTree(tree) : (
                    <tr><td colSpan="6" className="text-center text-muted" style={{ padding: 30 }}>No spans recorded</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {selectedSpan && (
            <div className="span-drawer">
              <div className="span-drawer-header">
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{selectedSpan.name}</div>
                  <div className="text-sm text-muted">{selectedSpan.type} &middot; {selectedSpan.span_id}</div>
                </div>
                <button className="btn-ghost btn-sm" onClick={() => setSelectedSpan(null)}>Close</button>
              </div>
              <div className="span-drawer-grid">
                <div className="span-drawer-stat">
                  <label>Duration</label>
                  <value>{formatDuration(selectedSpan.duration_ms)}</value>
                </div>
                <div className="span-drawer-stat">
                  <label>Tokens (in/out)</label>
                  <value>{selectedSpan.tokens_in} / {selectedSpan.tokens_out}</value>
                </div>
                <div className="span-drawer-stat">
                  <label>Cost</label>
                  <value>${(selectedSpan.cost || 0).toFixed(4)}</value>
                </div>
                <div className="span-drawer-stat">
                  <label>Status</label>
                  <value>{statusBadge(selectedSpan.status)}</value>
                </div>
              </div>
              {selectedSpan.error && (
                <div className="alert error" style={{ marginBottom: 12 }}>Error: {selectedSpan.error}</div>
              )}
              {selectedSpan.input && (
                <div style={{ marginBottom: 12 }}>
                  <div className="text-xs text-muted mb-2">Input</div>
                  <div className="code-block">{selectedSpan.input}</div>
                </div>
              )}
              {selectedSpan.output && (
                <div>
                  <div className="text-xs text-muted mb-2">Output</div>
                  <div className="code-block">{selectedSpan.output}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Traces</div>
          <div className="page-subtitle">Deep execution visibility for every agent run</div>
        </div>
        <div className="page-actions">
          <span className="text-sm text-muted">{total} traces</span>
        </div>
      </div>
      <div className="page-body">
        {stats && (
          <div className="metrics-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
            <div className="metric-card blue">
              <div className="metric-label">Total Traces (24h)</div>
              <div className="metric-value blue">{stats.total_traces}</div>
            </div>
            <div className="metric-card red">
              <div className="metric-label">Error Rate</div>
              <div className="metric-value red">{(stats.error_rate * 100).toFixed(1)}%</div>
            </div>
            <div className="metric-card green">
              <div className="metric-label">Avg Duration</div>
              <div className="metric-value green">{formatDuration(stats.avg_duration_ms)}</div>
            </div>
            <div className="metric-card purple">
              <div className="metric-label">Total Tokens</div>
              <div className="metric-value purple">{stats.total_tokens.toLocaleString()}</div>
            </div>
            <div className="metric-card yellow">
              <div className="metric-label">Total Cost</div>
              <div className="metric-value yellow">${stats.total_cost.toFixed(4)}</div>
            </div>
          </div>
        )}

        <div className="card">
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              placeholder="Search traces..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 300 }}
            />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ maxWidth: 160 }}>
              <option value="">All statuses</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="error">Error</option>
            </select>
          </div>

          {traces.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">&sim;</div>
              <h3>No traces yet</h3>
              <p>Send trace data from your agent using the SDK or API to see execution details here.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Agent</th>
                  <th>Status</th>
                  <th>Spans</th>
                  <th>Tokens</th>
                  <th>Cost</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {traces.map(t => (
                  <tr key={t.trace_id} className="clickable-row" onClick={() => openTrace(t.trace_id)}>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{t.name || 'trace'}</div>
                      <div className="text-xs text-mono text-muted">{t.trace_id.substring(0, 16)}...</div>
                    </td>
                    <td>{t.agent_name || '-'}</td>
                    <td>{statusBadge(t.status)}</td>
                    <td>{t.span_count}</td>
                    <td>{t.total_tokens.toLocaleString()}</td>
                    <td>{t.total_cost > 0 ? `$${t.total_cost.toFixed(4)}` : '-'}</td>
                    <td className="text-sm">{new Date(t.started_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

export default Traces;
